import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, take, timeout } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { TenantConfigService } from '../tenant/tenant-config.service';
import { TenantFeatureService } from '../tenant/tenant-feature.service';
import { TenantFeatureKey } from '../tenant/tenant-feature.model';

/**
 * Funktionaler Route-Guard der den Zugriff anhand von Berechtigungen oder Rollen schuetzt.
 *
 * Unterstuetzt drei Modi (koennen parallel in route.data stehen):
 *
 * 1. **Berechtigung-String (neues Modell, bevorzugt):**
 *    `data: { berechtigung: 'Auftraege:Lesen' }`
 *    Format: `'{Bereich}:{Lesen|Bearbeiten}'`
 *    SystemAdmin/TenantAdmin haben implizit alle Berechtigungen.
 *
 * 2. **Bereichs-Berechtigungen (Array-Form, alternativ):**
 *    `data: { berechtigungen: [{ bereich: 'Dashboard', aktion: 'lesen' }] }`
 *    Prueft ob der User mindestens eine der angegebenen Berechtigungen hat.
 *
 * 3. **System-Rollen (nur noch fuer SystemAdmin/TenantAdmin-only-Routen):**
 *    `data: { roles: ['SystemAdmin', 'TenantAdmin'] }`
 *    Prueft ob der User eine der angegebenen Rollen hat.
 *
 * Wenn mehrere definiert sind, reicht es wenn EINE der Pruefungen erfolgreich ist.
 */
export const roleGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot
): Promise<boolean> => {

  const authService      = inject(AuthService);
  const router           = inject(Router);
  const broadcastService = inject(MsalBroadcastService);
  const tenantConfig     = inject(TenantConfigService);
  const featureService   = inject(TenantFeatureService);

  // MSAL-Redirect wird bereits vom authGuard abgewartet (der immer vor roleGuard laeuft).
  // Hier nur kurz sicherstellen dass MSAL idle ist – sollte sofort resolven.
  try {
    await firstValueFrom(
      broadcastService.inProgress$.pipe(
        filter(status => status === InteractionStatus.None),
        take(1),
        timeout(500)
      )
    );
  } catch {
    // Timeout: MSAL haengt – weiter ohne Warten
  }

  // User laden – mit Retry falls der MsalInterceptor beim ersten Versuch
  // den Access-Token noch nicht bereit hat (typisch nach Login-Redirect).
  if (!authService.userGeladen && authService.isLoggedIn()) {
    await authService.ladeUser();

    // Retry: Falls ladeUser fehlschlug (401 weil Token noch nicht bereit),
    // kurz warten und nochmal versuchen. Kommt nur beim allerersten
    // Login-Redirect vor, danach ist der Token im Cache.
    if (!authService.userGeladen && !authService.apiNichtErreichbar) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      authService.clearUser(); // Cache zuruecksetzen damit ladeUser nochmal feuert
      await authService.ladeUser();
    }
  }

  // API nicht erreichbar → Fehlerseite statt Onboarding
  if (authService.apiNichtErreichbar) {
    router.navigate(['/api-fehler']);
    return false;
  }

  // TenantConfig neu laden wenn User eingeloggt ist und der Tenant nicht stimmt.
  // Beim App-Start wird die Config anonym geladen (erster Tenant als Fallback).
  // Nach dem Login kennen wir die tenantId des Users → Config mit richtiger tenantId laden.
  // KEIN tenantId-Parameter noetig: das Backend ermittelt den Tenant aus dem Auth-Token + DB.
  if (authService.userGeladen) {
    const aktuelleConfig = tenantConfig.getConfig();
    const userTenantId = authService.currentUser?.tenantId;
    if (userTenantId && aktuelleConfig.tenantId !== userTenantId) {
      await firstValueFrom(tenantConfig.loadConfig());
    }
  }

  // Eingeloggt aber User nicht in DB (kein Tenant zugeordnet) → Fehlerseite
  if (authService.isLoggedIn() && !authService.userGeladen) {
    router.navigate(['/kein-zugang']);
    return false;
  }

  // --- Feature-Check (vor Berechtigung-Check, Spec: UNIFIED_SECURITY_FEATURE_GATE.md) ---
  const featureKey: string | undefined = route.data?.['feature'];
  if (featureKey && !featureService.istAktiv(featureKey as TenantFeatureKey)) {
    router.navigate(['/unauthorized']);
    return false;
  }

  // --- Berechtigung-String pruefen (neues Modell, Format: 'Bereich:Lesen') ---
  const berechtigungString: string | undefined = route.data?.['berechtigung'];

  if (berechtigungString) {
    const hatBerechtigung = pruefeBerechtigungString(berechtigungString, authService);
    if (hatBerechtigung) return true;
  }

  // --- Bereichs-Berechtigungen pruefen (Array-Form) ---
  const berechtigungen: { bereich: string; aktion: 'lesen' | 'bearbeiten' }[] =
    route.data?.['berechtigungen'] ?? [];

  if (berechtigungen.length > 0) {
    const hatBerechtigung = berechtigungen.some(b =>
      authService.hatBerechtigung(b.bereich, b.aktion));
    if (hatBerechtigung) return true;
  }

  // --- Erlaubte Rollen aus den Routen-Daten lesen (nur noch SystemAdmin/TenantAdmin-Routen) ---
  const erlaubteRollen: string[] = route.data?.['roles'] ?? [];

  // Keine Pruefkriterien konfiguriert → Route ist oeffentlich
  if (!berechtigungString && berechtigungen.length === 0 && erlaubteRollen.length === 0) {
    return true;
  }

  // Rollenpruefung via AuthService (liest aus gecachtem AppUser)
  if (erlaubteRollen.length > 0 && authService.hasRole(erlaubteRollen)) {
    return true;
  }

  // Keine passende Rolle/Berechtigung → Weiterleitung zur Unauthorized-Seite
  router.navigate(['/unauthorized']);
  return false;
};

/**
 * Parst einen Berechtigung-String im Format 'Bereich:Lesen' oder 'Bereich:Bearbeiten'
 * und delegiert die Pruefung an AuthService.hatBerechtigung().
 *
 * Fuer ':Lesen'-Pruefungen reicht es wenn der User ':Bearbeiten' hat
 * (hoehere Berechtigung schliesst niedrigere ein).
 *
 * @param berechtigung String im Format 'Bereich:Lesen' oder 'Bereich:Bearbeiten'
 * @param authService Der AuthService fuer die Pruefung
 */
function pruefeBerechtigungString(berechtigung: string, authService: AuthService): boolean {
  const trennzeichen = berechtigung.indexOf(':');
  if (trennzeichen === -1) {
    // Ungueltig formatiert – als nicht berechtigt behandeln
    return false;
  }

  const bereich = berechtigung.substring(0, trennzeichen);
  const aktionRoh = berechtigung.substring(trennzeichen + 1).toLowerCase();
  const aktion: 'lesen' | 'bearbeiten' = aktionRoh === 'bearbeiten' ? 'bearbeiten' : 'lesen';

  return authService.hatBerechtigung(bereich, aktion);
}
